import { useCallback } from 'react'
import { HeaUnit, ModeTypeEnum } from '@types'
import styles from './GridUnitsLayout.module.scss'
import Card from '../unit-card/Card'
import DropBox from '../unit-card/DropBox'
import { useDrop } from 'react-dnd'
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  ResponderProvided,
  DraggableProvided,
  DroppableProvided,
  DraggableStateSnapshot,
} from 'react-beautiful-dnd'
import { LessonToConfirmFragment } from '@src/api/graphql-codegen'

type Props = {
  units?: HeaUnit[]
  source: number
  target?: string
  handleDrop?: (index: number, unit: HeaUnit) => void
  handleLeftArrowAction?: (unit: HeaUnit) => void
  handleRightArrowAction?: (unit: HeaUnit) => void
  handleOpenUnit?: (unit: HeaUnit) => void
  handleDnDOnStudyPlan?: (index: number, units: string[]) => void
  studyMode?: string
  confirmLessons?: LessonToConfirmFragment[]
}

const reorder = (list: any, startIndex: number, endIndex: number) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

function GridUnitsLayout(props: Props) {
  const { units, source, target, studyMode, confirmLessons, handleDrop, handleLeftArrowAction, handleRightArrowAction, handleOpenUnit, handleDnDOnStudyPlan } = props
  const onDrop = (unit: HeaUnit) => {
    handleDrop && handleDrop(source, unit)
  }
  const handleDragEnd = useCallback((result: DropResult, provided?: ResponderProvided) => {
    if (!result.destination) {
      return;
    }
    if (result.destination.index === result.source.index) {
      return;
    }
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    const updateUnits = reorder(units, sourceIndex, destIndex) as HeaUnit[]
    const unitIds = updateUnits.map((item: HeaUnit) => {
      return item.id
    })
    if (handleDnDOnStudyPlan) {
      handleDnDOnStudyPlan(source, unitIds)
    }
  }, [source, units, handleDnDOnStudyPlan]);

  const [{ canDrop }, drop] = useDrop({
    accept: "type",
    drop: onDrop,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })
  let index = source
  if (source === 0) {
    return (
      <div ref={drop} className={`${studyMode === ModeTypeEnum.Study ? styles.unitsDivStudyMode : styles.unitsDiv} ${canDrop ? styles.canDropbackground : ''}`}>
        {units?.map((unit, index) => (
          <Card
            key={index}
            type={"type"}
            source={source}
            target={target}
            unit={unit}
            isDragable={studyMode === ModeTypeEnum.Edit}
            handleLeftArrowAction={() => handleLeftArrowAction && handleLeftArrowAction(unit)}
            handleRightArrowAction={() => handleRightArrowAction && handleRightArrowAction(unit)}
            handleOpenUnit={handleOpenUnit}
            studyMode={studyMode}
            confirmLessons={confirmLessons}
          />
        ))}
      </div>
    )
  }
  return (
    <div ref={drop} className={styles.normalDiv }>
      <DropBox
        accept={"type"}
        source={source}
        onDrop={(item) => handleDrop && handleDrop(index, item)}
      />
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="droppableUnit" type="StudyPlanItems">
          {(droppableProvided: DroppableProvided) => (
            <div
              ref={droppableProvided.innerRef}
              {...droppableProvided.droppableProps}
            >
            {units?.map((unit, index) => (
              <Draggable
                key={unit.id}
                draggableId={unit.id}
                index={index}
              >
                {(
                  draggableProvided: DraggableProvided,
                  snapshot: DraggableStateSnapshot
                ) => {
                return (
                  <div
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                    >
                    <Card draggableProvided={draggableProvided} snapshot={snapshot} key={index} type={"type"} source={source} target={target} unit={unit} isDragable={true}
                      handleLeftArrowAction={() => handleLeftArrowAction && handleLeftArrowAction(unit)}
                      handleRightArrowAction={() => handleRightArrowAction && handleRightArrowAction(unit)}
                    />
                  </div>
                )
              }}
              </Draggable>
            ))}
            {droppableProvided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

export default GridUnitsLayout